// Redux-connected product checkbox row for the live intake form. Selection
// state lives in the selections slice; toggles also (re)schedule the debounced
// estimate call. All rendering is delegated to ProductCheckboxRowView so the
// admin branding preview can show the exact same UI with static state.
//
// This wrapper also resolves the one-way "included products" relationships:
//   - includedItems: the products THIS product brings along for free (shown as
//     an attached "Included at no charge" strip).
//   - includedBy / locked: whether THIS product is itself auto-included by some
//     other currently-selected product, in which case its card reads as
//     "Included" and its own checkbox is locked on (can't be deselected while
//     the parent stays selected). A manual selection is preserved underneath, so
//     deselecting the parent reverts to the user's own choice.
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleProduct, toggleProductOption, scheduleEstimate } from '../../store/selections-slice.js';
import { groupIncludesByParent, computeIncludedProducts } from '../../utils/product-includes.js';

import ProductCheckboxRowView from './ProductCheckboxRowView.jsx';

const EMPTY = [];

const ProductCheckboxRow = ({ product, options = [] }) => {
  const dispatch = useDispatch();
  const geometryToken = useSelector((s) => s.area.stats.data?.geometryToken ?? null);
  const items = useSelector((s) => s.selections.items);
  const includeRows = useSelector((s) => s.catalog.data?.includes ?? EMPTY);
  const allProducts = useSelector((s) => s.catalog.data?.products ?? EMPTY);
  const allOptions = useSelector((s) => s.catalog.data?.options ?? EMPTY);

  const item = items.find((i) => i.productId === product.id);

  const grouped = useMemo(() => groupIncludesByParent(includeRows), [includeRows]);
  const selectedProductIds = useMemo(() => items.map((i) => i.productId), [items]);
  const includedMap = useMemo(
    () => computeIncludedProducts(grouped, selectedProductIds),
    [grouped, selectedProductIds],
  );

  const productById = useMemo(() => new Map(allProducts.map((p) => [p.id, p])), [allProducts]);
  const optionById = useMemo(() => new Map(allOptions.map((o) => [o.id, o])), [allOptions]);

  // Products this product includes, with captured option names, for the strip.
  const includedItems = useMemo(() => (grouped.get(product.id) || [])
    .map((entry) => ({
      id: entry.includedProductId,
      name: productById.get(entry.includedProductId)?.name ?? '',
      optionNames: entry.optionIds
        .map((oid) => optionById.get(oid)?.name)
        .filter(Boolean),
    }))
    .filter((x) => x.name), [grouped, product.id, productById, optionById]);

  // Is this product currently auto-included by a selected parent? If so, by whom?
  const inclusion = includedMap.get(product.id);
  const includedBy = useMemo(() => (inclusion
    ? inclusion.parentIds.map((pid) => productById.get(pid)?.name).filter(Boolean)
    : null), [inclusion, productById]);
  const locked = Boolean(inclusion);

  const handleProductChange = useCallback(() => {
    // Locked-on while a parent that includes it stays selected.
    if (locked) return;
    dispatch(toggleProduct(product.id));
    dispatch(scheduleEstimate(geometryToken));
  }, [dispatch, product.id, geometryToken, locked]);

  // RadioGroup/Checkbox emit the value as a string; map it back to the original
  // option id (which may be numeric) rather than coercing, so any id type works.
  const handleOptionToggle = useCallback((rawValue) => {
    if (locked) return;
    const opt = options.find((o) => String(o.id) === rawValue);
    if (!opt) return;
    dispatch(toggleProductOption({
      productId: product.id, optionId: opt.id, mode: product.selection_mode,
    }));
    dispatch(scheduleEstimate(geometryToken));
  }, [dispatch, product.id, product.selection_mode, geometryToken, options, locked]);

  return (
    <ProductCheckboxRowView
      product={product}
      options={options}
      isChecked={!!item}
      selectedOptionIds={item?.optionIds ?? []}
      onProductChange={handleProductChange}
      onOptionToggle={handleOptionToggle}
      includedItems={includedItems}
      includedBy={includedBy}
      locked={locked}
    />
  );
};

export default ProductCheckboxRow;
